import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useNavigate } from 'react-router-dom';
import BrightnessLowTwoToneIcon from '@mui/icons-material/BrightnessLowTwoTone';
import DisplaySettingsTwoToneIcon from '@mui/icons-material/DisplaySettingsTwoTone';
import BusinessIcon from '@mui/icons-material/Business';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import AssistantIcon from '@mui/icons-material/Assistant';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import PublicIcon from '@mui/icons-material/Public';
import ReviewsIcon from '@mui/icons-material/Reviews';
import LogoutIcon from '@mui/icons-material/Logout';
import { AddCardRounded, PersonSearch, Badge, CreditCard } from '@mui/icons-material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ApartmentTwoToneIcon from '@mui/icons-material/ApartmentTwoTone';
import { useDispatch } from 'react-redux';
import { logout } from '../../../../redux/authSlice';
import { useAssistantAccess } from 'src/hooks/useAssistantAccess';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[100]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 0.2px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[100]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }

          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[100]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Un compte assistant n'a jamais accès qu'à Entreprises — le reste du menu est
  // entièrement absent (pas grisé) pour lui, cf. router.js pour le blocage réel.
  const { isAssistant, isSuperAdmin } = useAssistantAccess();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };
  const { closeSidebar } = useContext(SidebarContext);

  return (
    <>
      <MenuWrapper>
        {!isAssistant && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('nav.dashboard')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/dashboards/tasks"
                    startIcon={
                      <BrightnessLowTwoToneIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('nav.general')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('nav.management')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/companies"
                  startIcon={<BusinessIcon style={{ color: '#18E68E' }} />}
                >
                  {t('nav.companies')}
                </Button>
              </ListItem>
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/clients"
                    startIcon={<PersonSearch style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.clients')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/staff"
                    startIcon={<Badge style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.staff')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/suggestions"
                    startIcon={<AssistantIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.suggestions')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/contact-messages"
                    startIcon={<ContactSupportIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.contactMessages')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/BusinessArea"
                    startIcon={<AddBusinessIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.businessArea')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/countries"
                    startIcon={<PublicIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.countries')}
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>

        {/* Les deux grilles d'évaluation (personnel et service) regroupées dans leur
            propre section — réservée au super admin, comme avant dans Gestion. */}
        {!isAssistant && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('nav.evaluationSection')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/evaluation-criteria"
                    startIcon={<ReviewsIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.evaluationCriteria')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/services"
                    startIcon={<DesignServicesIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.services')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}

        {/* Les deux entrées de Paramètres sont réservées au super admin (l'ancien
            onglet Société n'était déjà pas affiché aux assistants) : sans ce garde,
            un assistant verrait un titre de section vide. */}
        {isSuperAdmin && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('nav.settings')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/company-settings"
                    startIcon={<ApartmentTwoToneIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('account.company')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/ai-prompts"
                    startIcon={<AutoAwesomeIcon style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.geminiConfig')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}

        {!isAssistant && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('nav.membershipsPayments')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/subscriptions"
                    startIcon={<AddCardRounded style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.subscriptions')}
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/card-payments"
                    startIcon={<CreditCard style={{ color: '#18E68E' }} />}
                  >
                    {t('nav.cardPayments')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('nav.accounts')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/settings"
                  startIcon={
                    <DisplaySettingsTwoToneIcon style={{ color: '#18E68E' }} />
                  }
                >
                  {t('nav.accountSettings')}
                </Button>
              </ListItem>
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/assistants"
                    startIcon={
                      <SupervisorAccountIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('nav.assistants')}
                  </Button>
                </ListItem>
              )}
              {!isAssistant && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/activity-log"
                    startIcon={
                      <HistoryIcon style={{ color: '#18E68E' }} />
                    }
                  >
                    {t('nav.activityLog')}
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('nav.account')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutIcon style={{ color: '#18E68E' }} />}
                >
                  {t('nav.logout')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
